import type React from 'react';
import { useTenderDetails } from '../../contexts/TenderDetailsContext';
import GenerateCVsSection from './GenerateCVsSection';
import WorkflowStep from './WorkflowStep';

const GenerateCVsStep: React.FC = () => {
  const {
    canManageTender,
    openStep,
    setOpenStep,
    generatedCVs,
    stepCompletion,
    toggleStepCompletion,
    validTenderId,
    tender,
    allCVs,
    cvGenerationStatus,
    cvServiceStartedAt,
    loadingStates,
    handleGenerateCVs,
    handleGenerateSingleCV,
    handleDownloadCV,
    handleDownload,
    handleCVDelete,
    handleDeleteSupportDoc,
    handleResetCVService,
    handleResetSingleCV,
    handleReportSingleCVError,
    isDraggingCV,
    handleDragEventsCV,
    handleDropCV,
    handleCVUpload,
    processingFiles,
    sectionsCount,
    selectedTemplates,
    handleTemplateChange,
    availableTemplates,
  } = useTenderDetails();

  if (!canManageTender) {
    return null;
  }

  const sortedCVs = [...allCVs].sort((a, b) => {
    const timeA = a.uploadedAt ? new Date(a.uploadedAt as string | number | Date).getTime() : 0;
    const timeB = b.uploadedAt ? new Date(b.uploadedAt as string | number | Date).getTime() : 0;
    if (timeB !== timeA) {
      return timeB - timeA;
    }
    const nameA = (a.originalFilename || a.name || '').toLowerCase();
    const nameB = (b.originalFilename || b.name || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });

  return (
    <WorkflowStep
      stepNumber={13}
      expanded={openStep === 13}
      onChange={(_, isExpanded) => setOpenStep(isExpanded ? 13 : null)}
      title="Generate CVs"
      description="Generate optimized CVs for this tender."
      isComplete={generatedCVs.length > 0 || !!stepCompletion[13]}
      onToggleComplete={() => toggleStepCompletion(13)}
      optional={true}
    >
      <GenerateCVsSection
        tenderId={validTenderId}
        usedCount={(tender.adhocDocCount || 0) + (tender.cvCount || 0)}
        inputCVs={sortedCVs}
        generatedCVs={generatedCVs}
        isGenerating={cvGenerationStatus === 'initializing' || cvGenerationStatus === 'processing'}
        cvGenerationStatus={cvGenerationStatus}
        cvServiceStartedAt={cvServiceStartedAt}
        loadingStates={loadingStates}
        onGenerate={handleGenerateCVs}
        onGenerateSingle={handleGenerateSingleCV}
        onDownloadInput={handleDownloadCV}
        onDownloadGenerated={(uri, name) => handleDownload(uri, `cv_generated_${name}`, name)}
        onDeleteInput={(doc) => handleCVDelete(doc, handleDeleteSupportDoc)}
        onResetCVService={handleResetCVService}
        onResetSingleCV={handleResetSingleCV}
        onReportSingleCVError={handleReportSingleCVError}
        isDragging={isDraggingCV}
        onDragEnter={handleDragEventsCV}
        onDragOver={handleDragEventsCV}
        onDragLeave={handleDragEventsCV}
        onDrop={handleDropCV}
        onFileUpload={handleCVUpload}
        processingFiles={processingFiles.support}
        isDraftGenerated={!!tender.draftFileLink || sectionsCount > 0}
        selectedTemplates={selectedTemplates}
        onTemplateChange={handleTemplateChange}
        availableTemplates={availableTemplates}
      />
    </WorkflowStep>
  );
};

export default GenerateCVsStep;
